import FJConstant from '../../constants/FJConstant';
import FJCanvasUtil from '../../util/FJCanvasUtil';
import FJUtil from '../../util/FJUtil';
import FJPoolStore from '../FJPoolStore';

export const BACKGROUND_TYPE = {
    IMAGE: 'image',
    COLOR: 'color',
    TRANSPARENT: 'transparent',
};

class FJVideoRemoveBaseManage {
    _canvas = null; // 画布
    _ctx = null; // 画布上下文

    _backgroundCanvas = null;
    _backgroundCtx = null;

    _backgroundImage = null;
    _backgroundImageLoaded = false;
    _backgroundImageRect = null;

    _backgroundInfo = {
        type: BACKGROUND_TYPE.TRANSPARENT,
        imageInfo: null,
        color: null,
    };

    _transparentSize = 14;

    _source = null; // 视频Video 标签
    _sourceInfo = null; // 视频源信息
    _sourceFile = null; // 视频源文件
    _sourceUrl = null; // 视频源url

    // 状态  FJConstant.LOADING 加载中 FJConstant.ERROR 加载错误 FJConstant.SUCCESS 加载成功
    _loadStatus = FJConstant.DEFAULT;
    _isPlaying = false;
    _duration = 0;

    constructor() {
        this._backgroundCanvas = FJPoolStore.getCanvas();
        this._backgroundCtx = this._backgroundCanvas.getContext('2d');
    }

    /**
     * @desc 跳转到指定帧
     * @param {Number} time
     */
    async seek(time, notDraw = false) {
        if (!this._source) return;
        const seeked = await new Promise(resolve => {
            this._source.removeEventListener('seeked', this._seekedListener);

            this._seekedListener = () => {
                resolve && resolve(true);
            };
            this._source.addEventListener('seeked', this._seekedListener);

            this._source.currentTime = time;
        });

        if (seeked) {
            !notDraw && this.draw();
        }
    }

    /**
     * @desc 绘制画面
     */
    async draw() {
        if (this._loadStatus !== FJConstant.SUCCESS || !this._source || !this._ctx) return;

        this._canvas.width = this._sourceInfo.width;
        this._canvas.height = this._sourceInfo.height;

        this._ctx.drawImage(this._source, 0, 0, this._sourceInfo.width, this._sourceInfo.height);
    }

    /**
     * @desc 播放视频
     */
    async play(callback, pauseCallback) {
        if (this._isPlaying) return;
        this._isPlaying = true;
        if (this._source) {
            let requestAnimationTime;
            this._playTime = this._source.currentTime;
            await this._source.play();

            let __playing = timestamp => {
                if (!requestAnimationTime) requestAnimationTime = timestamp;

                this._playTime += (timestamp - requestAnimationTime) / 1000;
                requestAnimationTime = timestamp;

                if (this._playTime >= 0 && this._playTime <= this._duration) {
                    callback && callback(this._playTime);
                    this.draw();
                }

                if (this._playTime >= this._duration) {
                    this._isPlaying && this.pause();
                    pauseCallback && pauseCallback(this._source.currentTime);
                } else {
                    this._requestTimer = window.requestAnimationFrame(__playing);
                }
            };

            if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
            this._requestTimer = window.requestAnimationFrame(__playing);
        }
    }

    /**
     * @desc 暂停视频
     */
    async pause() {
        if (!this._isPlaying || !this._source) return;

        if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
        await this._source.pause();
        this._isPlaying = false;
    }

    /**
     * @desc 设置音量
     * @param {Number} volume
     */
    setVolume(volume) {
        this._source.volume = volume;
    }

    /**
     * @desc 设置视频源
     */
    static setSource() {}

    /**
     * @desc 获取视频源
     * @returns {Object}
     */
    getSource() {
        return this._sourceFile;
    }

    clearAll() {
        this.pause();
        this._source = null;
        this._sourceInfo = null;
        this._sourceFile = null;
        this._loadStatus = FJConstant.DEFAULT;
        this._isPlaying = false;
        this._duration = 0;
        this._backgroundInfo = {
            type: BACKGROUND_TYPE.TRANSPARENT,
            imageInfo: null,
            color: null,
        };
    }

    /**
     * @desc 设置画布
     * @param {Object} canvas
     */
    setCanvas(canvas) {
        this._canvas = canvas;
        this._ctx = canvas.getContext('2d');
    }

    /**
     * @desc 获取视频源信息
     * @returns {Object}
     */
    getSourceInfo() {
        if (!this._sourceInfo) return null;
        return {
            width: this._sourceInfo.width,
            height: this._sourceInfo.height,
            duration: this._duration,
        };
    }

    getCurrentTime() {
        if (!this._source) return 0;
        return this._source.currentTime;
    }

    getIsPlaying() {
        return this._isPlaying;
    }

    getSize() {
        if (!this._sourceInfo) return null;
        return {
            width: this._sourceInfo.width,
            height: this._sourceInfo.height,
        };
    }

    updateBackgroundCanvas() {
        const size = this.getSize();
        if (!size) return;

        if (!this._backgroundCanvas) {
            this._backgroundCanvas = FJPoolStore.getCanvas('2d', true);
            this._backgroundCtx = this._backgroundCanvas.getContext('2d');
        }

        this._backgroundCanvas.width = size.width;
        this._backgroundCanvas.height = size.height;

        const { type, color } = this._backgroundInfo;

        switch (type) {
            case BACKGROUND_TYPE.TRANSPARENT: {
                const transparentSize = Math.round(
                    ((this._transparentSize / 1080) * size.width) / (size.width / size.height),
                );

                FJCanvasUtil.drawTransparentBackground(this._backgroundCtx, size.width, size.height, transparentSize);
                break;
            }
            case BACKGROUND_TYPE.COLOR: {
                this._backgroundCtx.save();
                this._backgroundCtx.fillStyle = color;
                this._backgroundCtx.fillRect(0, 0, size.width, size.height);
                this._backgroundCtx.restore();
                break;
            }
            case BACKGROUND_TYPE.IMAGE: {
                if (!this._backgroundImage) return;

                this._backgroundCtx.drawImage(
                    this._backgroundImage,
                    this._backgroundImageRect.x,
                    this._backgroundImageRect.y,
                    this._backgroundImageRect.width,
                    this._backgroundImageRect.height,
                );
                break;
            }
        }
    }

    getBackgroundInfo() {
        return this._backgroundInfo;
    }

    _fitBackgroundImage() {
        if (!this._backgroundImage) return;

        const size = this.getSize();
        const bgSize = {
            width: this._backgroundImage.width,
            height: this._backgroundImage.height,
        };

        const canvasAspect = size.width / size.height;
        const imageAspect = bgSize.width / bgSize.height;

        if (canvasAspect < imageAspect) {
            bgSize.height = size.height;
            bgSize.width = size.height * imageAspect;
        } else {
            bgSize.width = size.width;
            bgSize.height = size.width / imageAspect;
        }

        this._backgroundImageRect = {
            x: (size.width - bgSize.width) / 2,
            y: (size.height - bgSize.height) / 2,
            width: bgSize.width,
            height: bgSize.height,
        };
    }

    async setBackgroundImage(setBackgroundImage) {
        let backgroundImageUrl = setBackgroundImage.url;

        const isCrossOrigin = FJUtil.isCrossOrigin(backgroundImageUrl);

        if (isCrossOrigin) {
            // 下载图片
            backgroundImageUrl = await FJUtil.getBlobByUrlSolveCrossOrigin(backgroundImageUrl);
        }
        try {
            await new Promise((resolve, reject) => {
                // 判断editImageUrl 是否跨域
                this._backgroundImage = new Image();
                this._backgroundImage.src = backgroundImageUrl;

                this._backgroundImage.onload = () => {
                    this._backgroundImageLoaded = true;
                    resolve();
                };

                this._backgroundImage.onerror = () => {
                    reject();
                };
            });

            this._fitBackgroundImage();
            this._backgroundInfo.type = BACKGROUND_TYPE.IMAGE;
            this._backgroundInfo.imageInfo = setBackgroundImage;
            this._backgroundInfo.color = null;
            this.updateBackgroundCanvas();
        } catch (error) {
            this._backgroundImageLoaded = false;
            this._backgroundImage = null;
            throw error;
        }
    }

    setBackgroundColor(color) {
        this._backgroundInfo.type = BACKGROUND_TYPE.COLOR;
        this._backgroundInfo.color = color;
        this._backgroundInfo.imageInfo = null;
        this.updateBackgroundCanvas();
    }

    clearBackground() {
        this._backgroundInfo.type = BACKGROUND_TYPE.TRANSPARENT;
        this._backgroundInfo.color = null;
        this._backgroundInfo.imageInfo = null;
        this.updateBackgroundCanvas();
    }

    getSourceStatus() {
        return this._loadStatus;
    }
}
export default FJVideoRemoveBaseManage;
